// Legal copy for the Orion Fund site.
// NOTE: This is a starting draft modelled on common venture / digital-asset site terms.
// Items in [brackets] must be completed and the whole text reviewed by counsel before publishing.

export const LEGAL_ENTITY = 'Draper Dragon Orion Fund';
export const LEGAL_ENTITY_SHORT = 'the Orion Fund';
export const LEGAL_EMAIL = '[legal@draperdragon.com]';
export const GOVERNING_LAW = '[the State of California]';
export const VENUE = '[San Mateo County, California]';
export const LAST_UPDATED = 'September 26, 2026';

export const DISCLOSURES = [
  {
    title: 'No Offer, Recommendation or Solicitation',
    body: [
      `Nothing on this site constitutes an offer to sell, a solicitation of an offer to buy, or a recommendation of any security, digital asset, fund interest or other financial instrument by ${LEGAL_ENTITY}, Draper Dragon or any of their affiliates (together, "Orion", "we", "us" or "our"). Any such offer may only be made to qualified investors through definitive offering documents, and only in jurisdictions where it is lawful to do so.`,
    ],
  },
  {
    title: 'Not Investment, Legal or Tax Advice',
    body: [
      'The information on this site, including dashboard metrics, portfolio information and program descriptions, is provided for general informational and transparency purposes only. It is not investment, legal, tax or accounting advice. You should consult your own advisors before making any decision.',
    ],
  },
  {
    title: 'Forward-Looking Statements',
    body: [
      'Certain statements on this site, in our social media posts or in other public communications may be forward-looking. Words such as "may", "will", "should", "expect", "anticipate", "estimate", "intend", "plan", "target" and similar expressions identify these statements. They reflect current expectations only, involve risks and uncertainties, and actual results may differ materially. We undertake no obligation to update them.',
    ],
  },
  {
    title: 'Data & Third-Party Sources',
    body: [
      'On-chain metrics and prices are sourced from third parties including Dune Analytics, DefiLlama, Blockfrost, Binance and CoinGecko. This data has not been independently verified, may be delayed or incomplete, and may change without notice. Past activity and ecosystem metrics are not indicative of future results.',
    ],
  },
  {
    title: 'Social Media Disclaimer',
    body: [
      'Content we publish on X, LinkedIn and other channels is provided solely for informational purposes and is subject to the same disclaimers as this site. Orion, Draper Dragon and their affiliates, partners and personnel may hold positions in companies, digital assets or protocols we discuss.',
    ],
  },
];

export const TERMS = [
  {
    title: 'Acceptance of Terms',
    body: [
      `These Terms of Service ("Terms") govern your access to and use of the Orion Fund website, dashboard and related pages (the "Site"), operated by ${LEGAL_ENTITY} and its affiliates. By accessing or using the Site you agree to these Terms. If you do not agree, do not use the Site.`,
      'We may change, add or remove portions of these Terms at any time. Changes take effect when posted, and your continued use of the Site means you accept the updated Terms. Please review them periodically.',
    ],
  },
  ...DISCLOSURES.slice(0, 2),
  {
    title: 'No Investment Company Registration',
    body: [
      `${LEGAL_ENTITY} is not, and does not expect to be, registered as an investment company under the U.S. Investment Company Act of 1940, as amended, and visitors to the Site are not entitled to the protections of that Act. Interests in the fund have not been registered under the U.S. Securities Act of 1933 or any state or foreign securities laws.`,
    ],
  },
  DISCLOSURES[2],
  DISCLOSURES[3],
  {
    title: 'Portfolio & Program Information',
    body: [
      'References to portfolio companies, accelerator participants, team members or partners are for informational purposes only and do not represent all investments made by the fund. Inclusion on the Site is not an endorsement or a recommendation of any company, product or token. Confidential terms, valuations and transaction details are not disclosed.',
    ],
  },
  {
    title: 'Intellectual Property',
    body: [
      'The Site and its content, including text, graphics, logos and software, are owned by us or our licensors and are protected by intellectual property laws. Third-party names and logos are the property of their respective owners and are used for identification only. You may view the Site for personal, non-commercial purposes; any other use requires our prior written consent.',
    ],
  },
  {
    title: 'Acceptable Use',
    body: [
      'You agree not to misuse the Site, including by attempting to gain unauthorized access, interfering with its operation, scraping it at a rate that burdens our systems or third-party data providers, or using it for any unlawful purpose.',
    ],
  },
  {
    title: 'Linked Sites',
    body: [
      'The Site links to third-party websites, including those of portfolio companies, partners and data providers. We do not control and are not responsible for their content, availability or privacy practices. A link does not imply endorsement. Your use of third-party sites is at your own risk and subject to their terms.',
    ],
  },
  {
    title: 'Disclaimer of Warranties',
    body: [
      'The Site is provided "as is" and "as available", without warranties of any kind, express or implied, including warranties of accuracy, completeness, merchantability, fitness for a particular purpose or non-infringement. We do not warrant that the Site will be uninterrupted, error-free or free of harmful components.',
    ],
  },
  {
    title: 'Limitation of Liability',
    body: [
      'To the fullest extent permitted by law, none of Orion, Draper Dragon or their affiliates, partners, officers, employees or agents will be liable for any direct, indirect, incidental, consequential or other damages arising from your use of, or inability to use, the Site or any information on it, even if advised of the possibility of such damages.',
    ],
  },
  {
    title: 'Governing Law and Jurisdiction',
    body: [
      `These Terms and your use of the Site are governed by the laws of the United States of America and ${GOVERNING_LAW}, without regard to conflict-of-laws principles. Any legal action relating to the Site shall be brought exclusively in the federal or state courts located in ${VENUE}.`,
    ],
  },
  DISCLOSURES[4],
  {
    title: 'Contact',
    body: [`Questions about these Terms can be sent to ${LEGAL_EMAIL}.`],
  },
];

export const PRIVACY = [
  {
    title: 'Overview',
    body: [
      `This Privacy Policy explains how ${LEGAL_ENTITY} and its affiliates ("we", "us") collect, use and share information when you visit the Orion Fund website and dashboard (the "Site") or contact us. By using the Site you agree to this Policy.`,
    ],
  },
  {
    title: 'Information We Collect',
    body: [
      'Information you provide: when you submit a contact, pitch or feedback form, email us or apply to a program, we collect the details you choose to share, such as your name, email address, company, role and any information about your project.',
      'Information collected automatically: like most websites, we and our service providers may collect technical information such as IP address, browser and device type, pages viewed, referring URLs and approximate location. The Site also stores small preferences (for example your theme, currency and language) and short-lived data caches in your browser.',
    ],
  },
  {
    title: 'How We Use Information',
    body: [
      'We use information to respond to enquiries and pitches, evaluate potential investments and program applicants, operate, secure and improve the Site, understand how the Site is used, comply with legal obligations, and communicate with you about the Orion Fund where you have asked us to.',
    ],
  },
  {
    title: 'How We Share Information',
    body: [
      'We do not sell your personal information. We may share it with Draper Dragon affiliates and program partners (such as Draper University) where relevant to your enquiry; with service providers that host or support the Site and our forms (such as Supabase and analytics or security providers) under confidentiality obligations; and where required by law or to protect our rights, users or others.',
    ],
  },
  {
    title: 'Third-Party Services',
    body: [
      'The Site loads data from third-party APIs (including Dune Analytics, DefiLlama, Blockfrost, Binance and CoinGecko) and fonts from Google Fonts, and embeds third-party forms. These providers may receive technical information such as your IP address when your browser connects to them and handle it under their own privacy policies.',
    ],
  },
  {
    title: 'Data Retention & Security',
    body: [
      'We keep personal information only as long as needed for the purposes above or as required by law. We use reasonable administrative, technical and physical safeguards, but no method of transmission or storage is completely secure.',
    ],
  },
  {
    title: 'Your Choices & Rights',
    body: [
      'Depending on where you live, you may have rights to access, correct, delete or restrict the use of your personal information, or to object to certain processing. You can also clear the preferences stored in your browser at any time. To make a request, contact us at the address below.',
    ],
  },
  {
    title: 'International Visitors',
    body: [
      'We operate from the United States and work with partners internationally. Your information may be transferred to and processed in countries whose data-protection laws differ from those in your country.',
    ],
  },
  {
    title: "Children's Privacy",
    body: ['The Site is not directed to children under 16, and we do not knowingly collect their personal information.'],
  },
  {
    title: 'Changes to This Policy',
    body: ['We may update this Policy from time to time. The "Last updated" date shows when it last changed; continued use of the Site means you accept the updated Policy.'],
  },
  {
    title: 'Contact',
    body: [`Privacy questions or requests can be sent to ${LEGAL_EMAIL}.`],
  },
];
